import { useEffect, useState } from "react";
import About from "./About";
import Projects from "./Projects";
import Contact from "./Contact";

const skills = [
  "Node.js",
  "JavaScript",
  "TypeScript",
  "Express.js",
  "NestJS",
  "PostgreSQL",
  "MySQL",
  "MongoDB",
  "Redis",
  "Kafka",
  "Socket.IO",
  "Docker",
  "REST APIs",
  "Microservices",
  "CI/CD"
];

const stats = [
  { value: "3+", label: "Years Experience" },
  { value: "40%", label: "Performance Improved" },
  { value: "99.9%", label: "Reliability Focus" },
  { value: "24/7", label: "Backend Mindset" }
];

const socialLinks = [
  {
    label: "GitHub",
    href: "https://github.com/akshayecs"
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/in/software-engineer-akshayprajapati"
  },
  {
    label: "Email",
    href: "mailto:akshayprajapati2897@gmail.com"
  }
];

function ArrowIcon() {
  return <span aria-hidden="true">↗</span>;
}

function CodeIcon() {
  return (
    <span className="brand-mark" aria-hidden="true">
      &lt;/&gt;
    </span>
  );
}

export default function Home() {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") {
      return "dark";
    }

    return localStorage.getItem("portfolio-theme") || "dark";
  });

  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("portfolio-theme", theme);
  }, [theme]);

  useEffect(() => {
    const closeMenu = () => {
      setMenuOpen(false);
    };

    window.addEventListener("resize", closeMenu);

    return () => {
      window.removeEventListener("resize", closeMenu);
    };
  }, []);

  const toggleTheme = () => {
    setTheme((current) => (current === "dark" ? "light" : "dark"));
  };

  const scrollTo = (id) => {
    setMenuOpen(false);

    document
      .getElementById(id)
      ?.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
  };

  return (
    <div className="site-shell">

      {/* ================= HEADER ================= */}

      <header className="site-header">
        <div className="container nav-wrap">

          <a
            className="logo"
            href="#home"
            aria-label="Akshay Prajapati home"
          >
            <CodeIcon />

            <span>
              Akshay<span className="accent-text">.</span>
            </span>
          </a>

          <button
            className="mobile-menu"
            type="button"
            aria-label={
              menuOpen
                ? "Close navigation"
                : "Open navigation"
            }
            aria-expanded={menuOpen}
            onClick={() =>
              setMenuOpen((open) => !open)
            }
          >
            <span />
            <span />
            <span />
          </button>

          <nav
            className={`nav-links ${
              menuOpen ? "is-open" : ""
            }`}
            aria-label="Primary navigation"
          >
            <button onClick={() => scrollTo("home")}>
              Home
            </button>

            <button onClick={() => scrollTo("about")}>
              About
            </button>

            <button onClick={() => scrollTo("projects")}>
              Projects
            </button>

            <button onClick={() => scrollTo("contact")}>
              Contact
            </button>

            <a
              className="nav-resume"
              href="/Akshay_Prajapati_NodeJS.pdf"
              download
            >
              Resume
            </a>
          </nav>

          <button
            className="theme-toggle"
            type="button"
            onClick={toggleTheme}
            aria-label={`Switch to ${
              theme === "dark"
                ? "light"
                : "dark"
            } theme`}
            title={`Switch to ${
              theme === "dark"
                ? "light"
                : "dark"
            } theme`}
          >
            <span aria-hidden="true">
              {theme === "dark" ? "☀" : "☾"}
            </span>
          </button>

        </div>
      </header>

      {/* ================= MAIN ================= */}

      <main>

        {/* ================= HERO ================= */}

        <section
          id="home"
          className="hero-section"
        >

          <div
            className="hero-grid"
            aria-hidden="true"
          />

          <div className="container hero-content">

            <div className="hero-copy">

              <p className="eyebrow">
                <span className="status-dot" />

                Available for backend opportunities
              </p>

              <h1>
                Building{" "}
                <span className="gradient-text">
                  scalable
                </span>{" "}
                backend systems that are built to last.
              </h1>

              <p className="hero-description">
                I'm{" "}
                <strong>
                  Akshay Prajapati
                </strong>
                , a Backend Developer focused on
                Node.js, databases, distributed
                systems, APIs and cloud-ready
                application architecture.
              </p>

              <div className="hero-actions">

                <button
                  className="button button-primary"
                  onClick={() =>
                    scrollTo("projects")
                  }
                >
                  Explore Projects{" "}
                  <ArrowIcon />
                </button>

                <a
                  className="button button-secondary"
                  href="/Akshay_Prajapati_NodeJS.pdf"
                  download
                >
                  Download Resume{" "}
                  <span aria-hidden="true">
                    ↓
                  </span>
                </a>

              </div>

              <div className="social-row">

                {socialLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    target={
                      link.href.startsWith("http")
                        ? "_blank"
                        : undefined
                    }
                    rel={
                      link.href.startsWith("http")
                        ? "noreferrer"
                        : undefined
                    }
                  >
                    {link.label}
                  </a>
                ))}

              </div>

            </div>

            {/* ================= HERO VISUAL ================= */}

            <div
              className="hero-visual"
              aria-label="Developer profile"
            >

              <div
                className="orbit orbit-one"
                aria-hidden="true"
              />

              <div
                className="orbit orbit-two"
                aria-hidden="true"
              />

              <div
                className="profile-glow"
                aria-hidden="true"
              />

              <div className="profile-card">

                <div className="profile-top">

                  <span className="window-dot" />
                  <span className="window-dot" />
                  <span className="window-dot" />

                  <span className="profile-file">
                    backend.js
                  </span>

                </div>

                <div
                  className="code-preview"
                  aria-hidden="true"
                >

                  <p>
                    <span className="code-purple">
                      const
                    </span>{" "}
                    developer{" "}
                    <span className="code-white">
                      =
                    </span>{" "}
                    {"{"}
                  </p>

                  <p>
                    &nbsp;&nbsp;name:{" "}
                    <span className="code-green">
                      "Akshay"
                    </span>
                    ,
                  </p>

                  <p>
                    &nbsp;&nbsp;role:{" "}
                    <span className="code-green">
                      "Backend Developer"
                    </span>
                    ,
                  </p>

                  <p>
                    &nbsp;&nbsp;stack: [
                    <span className="code-green">
                      "Node.js"
                    </span>
                    ,{" "}
                    <span className="code-green">
                      "Postgres"
                    </span>
                    ],
                  </p>

                  <p>
                    &nbsp;&nbsp;focus:{" "}
                    <span className="code-green">
                      "Scale & Reliability"
                    </span>
                  </p>

                  <p>
                    {"};"}
                  </p>

                  <p className="code-comment">
                    {"// build. ship. improve."}
                  </p>

                </div>

                <div className="profile-footer">

                  <span>
                    ● Open to opportunities
                  </span>

                  <span>
                    3+ yrs
                  </span>

                </div>

              </div>

            </div>

          </div>

          {/* ================= STATS ================= */}

          <div className="container stats-grid">

            {stats.map((stat) => (
              <div
                className="stat-card"
                key={stat.label}
              >
                <strong>
                  {stat.value}
                </strong>

                <span>
                  {stat.label}
                </span>
              </div>
            ))}

          </div>

        </section>

        {/* ================= SKILLS ================= */}

        <section
          className="skills-strip"
          aria-label="Technology skills"
        >

          <div className="container">

            <div className="section-kicker">
              Core technologies
            </div>

            <div className="skill-list">

              {skills.map((skill) => (
                <span key={skill}>
                  {skill}
                </span>
              ))}

            </div>

          </div>

        </section>

        {/* ================= SECTIONS ================= */}

        <About />

        <Projects />

        <Contact />

      </main>

      {/* ================= FOOTER ================= */}

      <footer className="site-footer">

        <div className="container footer-content">

          <div>

            <a
              className="logo"
              href="#home"
            >
              <CodeIcon />

              <span>
                Akshay
                <span className="accent-text">
                  .
                </span>
              </span>
            </a>

            <p>
              Backend development • APIs •
              scalable systems
            </p>

          </div>

          <div className="footer-links">

            {socialLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target={
                  link.href.startsWith("http")
                    ? "_blank"
                    : undefined
                }
                rel={
                  link.href.startsWith("http")
                    ? "noreferrer"
                    : undefined
                }
              >
                {link.label}
              </a>
            ))}

          </div>

          <p className="copyright">
            © {new Date().getFullYear()} Akshay Prajapati
          </p>

        </div>

      </footer>

    </div>
  );
}